import { BookOpen, ExternalLink, Eye, EyeOff } from "lucide-react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type AdminCourse,
  useAdminCourses,
  useToggleCoursePublished,
} from "@/hooks/use-admin-courses";

function PublishedBadge({ published }: { published: boolean }) {
  return (
    <span
      className={
        published
          ? "inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-green-600/20"
          : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-border"
      }
    >
      {published ? "Published" : "Unpublished"}
    </span>
  );
}

function CourseRow({ course }: { course: AdminCourse }) {
  const toggle = useToggleCoursePublished();

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3">
        <div className="font-medium text-foreground">{course.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{course.slug}</div>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">{course.totalModules} modules</td>
      <td className="px-4 py-3">
        <PublishedBadge published={course.isPublished} />
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-2">
          {course.previewUrl && (
            <a
              href={course.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
            >
              <ExternalLink className="h-3 w-3" />
              Preview
            </a>
          )}
          <button
            type="button"
            disabled={toggle.isPending}
            onClick={() => toggle.mutate({ courseId: course.id, isPublished: !course.isPublished })}
            className={
              course.isPublished
                ? "inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted disabled:opacity-50"
                : "inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
            }
          >
            {course.isPublished ? (
              <>
                <EyeOff className="h-3 w-3" />
                Unpublish
              </>
            ) : (
              <>
                <Eye className="h-3 w-3" />
                Publish
              </>
            )}
          </button>
        </div>
      </td>
    </tr>
  );
}

export function CoursesTab() {
  const { data: courses, isLoading, isError, error, refetch } = useAdminCourses();

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load courses"}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle
            style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, color: "#0f1944" }}
          >
            Course Catalog
          </CardTitle>
          <CardDescription>Control which courses are visible to users.</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            headers={[
              { label: "Course" },
              { label: "Modules" },
              { label: "Status" },
              { label: "Actions", className: "text-right whitespace-nowrap" },
            ]}
            loading={isLoading}
            empty={
              <EmptyState
                icon={BookOpen}
                title="No courses found"
                description="Courses are seeded on startup."
              />
            }
          >
            {courses?.map((course) => (
              <CourseRow key={course.id} course={course} />
            ))}
          </DataTable>
        </CardContent>
      </Card>
    </div>
  );
}
